import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { makeStyles } from '@mui/styles';
import { toast } from 'react-toastify';
import noFile from '../../images/drag-drop.png';
import fileUploaded from '../../images/uploaded.png';
import PropTypes from 'prop-types';
import 'react-toastify/dist/ReactToastify.css';
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const useStyles = makeStyles((theme) => ({
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: '2px dashed #4a90e2',
    borderRadius: '10px',
    justifyContent: 'center',
    // padding: '16px',
    minHeight: "314px",
    textAlign: 'center',
    backgroundColor: '#152B3A',
    transition: 'border 0.24s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#5aadee',
    },
  },
  image: {
    maxHeight: '164px',
    maxWidth: '164px',
  },
  content: {
    color: '#fff',
    fontSize: '16px',
  },
  verifyContent: {
    color: '#28a745', // Green color for verified
    fontSize: '16px',
  },
  errorContent: {
    color: '#e74c3c',
    fontSize: '16px',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const FileDragDrop = ({ onImport, id, acceptedFileType = '.json', valid = null, isFileUploadedProp }) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    setIsFileUploaded(isFileUploadedProp);
  }, [isFileUploadedProp]);

  const handleFileRead = (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setIsLoading(true); // Start showing loading spinner
      const file = acceptedFiles[0];
      setFileName(file.name);

      // Simulating a 5-second loading delay before parsing the file
      setTimeout(() => {
        handleFileRead(file)
          .then((content) => {
            try {
              const data = JSON.parse(content); // Parse the JSON content
              setIsFileUploaded(true);
              setIsLoading(false); // Hide the loading spinner
              onImport && onImport(data, file);
              toast.success(`${file.name} uploaded successfully!`);
            } catch (err) {
              setIsLoading(false);
              setIsFileUploaded(false);
              onImport && onImport(null, file,)
              toast.error(`Failed to import file: Invalid JSON format. Please check the file and try again.`);
            }
          })
          .catch(() => {
            setIsLoading(false);
            toast.error('Cannot import file: An error occurred while reading the file.');
          });
      }, 5000); // Set a 5-second timeout
    } else {
      toast.error('No files were dropped or the file type is not supported.');
    }
  }, [onImport]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileType,
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={classes.loadingContainer}>
          <div className={classes.loading}>
            <CircularProgress />
          </div>

          <p className={classes.content}>Uploading file...</p>
        </div>
      );
    }
    if (isFileUploaded) {
      return (
        <div className={classes.uploadedContainer}>
          <img src={fileUploaded} className={classes.image} alt="Empty state" />
          <p className={classes.content}>
            {fileName} uploaded successfully!
          </p>
          {valid === true && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CheckIcon
                className={classes.icon}
                style={{ color: 'green' }}
              />
              <p className={classes.verifyContent}>
                {fileName} credentials verified.
              </p>
            </div>
          )}
          {valid === false && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CloseIcon
                className={classes.icon}
                style={{ color: 'red' }}
              />
              <p className={classes.errorContent}>
                {fileName} is an invalid file.
              </p>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className={classes.emptyContainer}>
        <img src={noFile} className={classes.image} alt="Empty state" />
        <p className={classes.content}>
          Drag and drop or select a key file obtained from GCP Console
        </p>
      </div>
    );
  };

  return (
    <div {...getRootProps({ className: classes.dropzone })}>
      <input {...getInputProps()} disabled={isLoading || isFileUploaded} />
      {renderContent()}
    </div>
  );
};

// Define PropTypes for the component
FileDragDrop.propTypes = {
  onImport: PropTypes.func,
  id: PropTypes.string,
  acceptedFileType: PropTypes.string,
  valid: PropTypes.bool,
  isFileUploadedProp: PropTypes.bool,
};

export default FileDragDrop;
